// Catalog.ID Client — Key Grant Policy Store
// ============================================================================
//
// Persistent "Allow always" policy entries per (requesterId, keyId), plus
// in-memory "Allow for this session" entries. Scoped to the currently-active
// CID; switching identities reveals a different set.
//
// Stored in localStorage (same tier as the shell layout blob — no server
// roundtrip needed on startup; policies are intrinsically device-local).
//
// The Connected Modules pane reads and mutates this; the Key Grant handler
// consults it before deciding whether to prompt.
// ============================================================================

const STORAGE_PREFIX = 'cid_key_grants_v1_';

export type PolicyScope = 'always' | 'session' | 'once';

export interface GrantPolicyEntry {
    requesterId: string;
    keyId: string;
    scope: Exclude<PolicyScope, 'once'>;
    grantedAt: string;  // ISO-8601
    label?: string;     // human-readable requester name ("XRPL Wallet", etc.)
    purpose?: string;   // purpose string from the original request
}

const sessionGrants = new Map<string, GrantPolicyEntry>();

function storageKey(cid: string): string {
    return `${STORAGE_PREFIX}${cid}`;
}

function entryKey(requesterId: string, keyId: string): string {
    return `${requesterId}::${keyId}`;
}

function loadPersistent(cid: string): GrantPolicyEntry[] {
    try {
        const raw = localStorage.getItem(storageKey(cid));
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isEntry);
    } catch {
        return [];
    }
}

function isEntry(x: unknown): x is GrantPolicyEntry {
    return typeof x === 'object' && x !== null
        && typeof (x as GrantPolicyEntry).requesterId === 'string'
        && typeof (x as GrantPolicyEntry).keyId === 'string'
        && ((x as GrantPolicyEntry).scope === 'always' || (x as GrantPolicyEntry).scope === 'session');
}

function savePersistent(cid: string, entries: GrantPolicyEntry[]): void {
    try {
        localStorage.setItem(storageKey(cid), JSON.stringify(entries));
    } catch {
        // Storage quota or disabled — log once elsewhere; a silent fallback is OK
        // for v1 because the grant still proceeds in session scope.
    }
}

/**
 * Check the effective policy for a given (requesterId, keyId) under the given CID.
 * Returns "always" if a persistent grant exists, "session" if an in-memory grant
 * exists for this session, otherwise "none".
 */
export function checkPolicy(cid: string, requesterId: string, keyId: string): 'always' | 'session' | 'none' {
    const key = entryKey(requesterId, keyId);
    const sessionEntry = sessionGrants.get(`${cid}::${key}`);
    if (sessionEntry) return 'session';

    const persistent = loadPersistent(cid).find(e => e.requesterId === requesterId && e.keyId === keyId);
    return persistent ? 'always' : 'none';
}

/** Record an "Allow always" decision. Persists to localStorage. */
export function grantAlways(cid: string, entry: Omit<GrantPolicyEntry, 'scope' | 'grantedAt'>): void {
    const next: GrantPolicyEntry = {
        ...entry,
        scope: 'always',
        grantedAt: new Date().toISOString(),
    };
    const existing = loadPersistent(cid).filter(
        e => !(e.requesterId === entry.requesterId && e.keyId === entry.keyId)
    );
    existing.push(next);
    savePersistent(cid, existing);
}

/** Record an "Allow for this session" decision. In-memory only. */
export function grantSession(cid: string, entry: Omit<GrantPolicyEntry, 'scope' | 'grantedAt'>): void {
    sessionGrants.set(`${cid}::${entryKey(entry.requesterId, entry.keyId)}`, {
        ...entry,
        scope: 'session',
        grantedAt: new Date().toISOString(),
    });
}

/** Revoke an "Allow always" entry. No-op if it doesn't exist. */
export function revoke(cid: string, requesterId: string, keyId: string): void {
    const filtered = loadPersistent(cid).filter(
        e => !(e.requesterId === requesterId && e.keyId === keyId)
    );
    savePersistent(cid, filtered);
    sessionGrants.delete(`${cid}::${entryKey(requesterId, keyId)}`);
}

/** List all "Allow always" entries for a CID — used by the Connected Modules pane. */
export function listAlways(cid: string): GrantPolicyEntry[] {
    return loadPersistent(cid);
}

/**
 * Clear in-memory session grants. Intended for logout/lock paths so that a
 * subsequent user on the same device starts fresh.
 */
export function clearSessionGrants(): void {
    sessionGrants.clear();
}

/**
 * Drop ONE identity's decisions entirely — the persisted "Allow always"
 * entries as well as its in-memory session grants. Only "Forget this device"
 * does this: an ordinary logout deliberately keeps persistent grants, which are
 * the user's explicit cross-session choices.
 */
export function clearAllGrants(cid: string): void {
    try {
        localStorage.removeItem(storageKey(cid));
    } catch {
        // Storage disabled — nothing persisted to remove.
    }
    const prefix = `${cid}::`;
    for (const k of Array.from(sessionGrants.keys())) {
        if (k.startsWith(prefix)) sessionGrants.delete(k);
    }
}
